import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import logo from '../assets/images/logo.svg';

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Handle scroll state
      setIsScrolled(window.scrollY > 10);

      // Handle active section
      const sections = ['home', 'about', 'services', 'what-we-do', 'contact'];
      let currentSection = '';

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/#about' },
    { 
      name: 'Products', 
      href: '/#products',
      dropdown: [
        { name: 'Sultansoft Academy', href: '/#products', description: 'Online learning platform' },
        { name: 'Shaka', href: '/#products', description: 'African trading platform' },
        { name: 'Blockchain & Telco', href: '/#products', description: 'Web3 & telecommunications' },
      ]
    },
    { name: 'Technology', href: '/#technology' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/#contact' },
  ];

  const headerVariants = {
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    hidden: {
      y: -100,
      opacity: 0,
    },
  };

  const mobileMenuVariants = {
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        ease: 'easeOut',
        staggerChildren: 0.1,
      },
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
    },
  };

  const menuItemVariants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    closed: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
    },
  };

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/' && !activeSection;
    }
    if (href.startsWith('/#')) {
      return activeSection === href.substring(2);
    }
    return location.pathname === href;
  };

  return (
    <motion.header
      initial="hidden"
      animate="visible"
      variants={headerVariants}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/">
              <img
                className="h-12 w-auto"
                src={logo}
                alt="Sultansoft"
              />
            </Link>
          </motion.div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-1">
              {navigation.map((item) => (
                <motion.div 
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.href.startsWith('/#') ? (
                    <a
                      href={item.href}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        isActive(item.href)
                          ? 'text-crimson-600 bg-crimson-50'
                          : 'text-gray-700 hover:text-crimson-600 hover:bg-crimson-50'
                      }`}
                    >
                      {item.name}
                      {item.dropdown && (
                        <svg className="inline-block w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        isActive(item.href)
                          ? 'text-crimson-600 bg-crimson-50'
                          : 'text-gray-700 hover:text-crimson-600 hover:bg-crimson-50'
                      }`}
                    >
                      {item.name}
                      {item.dropdown && (
                        <svg className="inline-block w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {item.dropdown && activeDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50"
                    >
                      {item.dropdown.map((dropdownItem, index) => (
                        <a
                          key={index}
                          href={dropdownItem.href}
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-600 transition-colors"
                        >
                          <div className="font-medium">{dropdownItem.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{dropdownItem.description}</div>
                        </a>
                      ))}
                    </motion.div>
                  )}
                </motion.div>
              ))}
              <motion.a
                href="#contact"
                className="ml-4 px-6 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-crimson-600 to-crimson-500 text-white hover:from-crimson-700 hover:to-crimson-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Demo
              </motion.a>
            </div>
          </div>

          {/* Mobile menu button */}
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex md:hidden items-center justify-center p-2 rounded-md text-gray-900 hover:text-blue-600 focus:outline-none"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="sr-only">Open main menu</span>
            {isOpen ? (
              <XIcon className="block h-6 w-6" />
            ) : (
              <MenuIcon className="block h-6 w-6" />
            )}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={mobileMenuVariants}
              className="md:hidden bg-white absolute left-0 right-0 shadow-lg rounded-b-2xl overflow-hidden"
            >
              <div className="px-4 pt-2 pb-3 space-y-1">
                {navigation.map((item) => (
                  <motion.div key={item.name} variants={menuItemVariants}>
                    {item.dropdown ? (
                      <div>
                        <div className="block px-4 py-3 rounded-lg text-base font-medium text-gray-900">
                          {item.name}
                        </div>
                        <div className="ml-4 space-y-1">
                          {item.dropdown.map((dropdownItem, index) => (
                            <a
                              key={index}
                              href={dropdownItem.href}
                              className="block px-4 py-2 rounded-lg text-sm text-gray-600 hover:text-crimson-600 hover:bg-crimson-50 transition-colors"
                              onClick={() => setIsOpen(false)}
                            >
                              {dropdownItem.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : item.href.startsWith('/#') ? (
                      <a
                        href={item.href}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive(item.href)
                            ? 'text-crimson-600 bg-crimson-50'
                            : 'text-gray-900 hover:text-crimson-600 hover:bg-crimson-50'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive(item.href)
                            ? 'text-crimson-600 bg-crimson-50'
                            : 'text-gray-900 hover:text-crimson-600 hover:bg-crimson-50'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
                <motion.a
                  href="#contact"
                  className="block px-4 py-3 rounded-lg text-base font-medium bg-gradient-to-r from-crimson-600 to-crimson-500 text-white hover:from-crimson-700 hover:to-crimson-600 transition-colors"
                  onClick={() => setIsOpen(false)}
                  variants={menuItemVariants}
                >
                  Request Demo
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}

export default Header;